// ─── SURVAIHEALTH DATA LAYER ──────────────────────────────────────────────────
import { supabase } from './supabaseClient';
import { API_BASE } from './apiBase';

const ls = {
  get: (key, def = null) => { try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : def; } catch { return def; } },
  set: (key, val) => { try { localStorage.setItem(key, JSON.stringify(val)); } catch {} },
};

// UUID validator — Supabase rejects non-UUID ids silently
function isUUID(str) {
  return typeof str === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);
}

// ─── FACILITIES ───────────────────────────────────────────────────────────────
export async function getFacilities() {
  if (!supabase) return ls.get('autopip_facilities', []);
  const { data, error } = await supabase.from('facilities').select('*').order('facility_name');
  if (error) { console.error('getFacilities:', error.message); return ls.get('autopip_facilities', []); }
  return data || [];
}

export async function saveFacility(facility) {
  if (!supabase) {
    const all = ls.get('autopip_facilities', []);
    const idx = all.findIndex(f => f.id === facility.id);
    if (idx >= 0) all[idx] = facility; else all.push(facility);
    ls.set('autopip_facilities', all);
    return facility;
  }
  if (!isUUID(facility.id)) {
    console.warn('Skipping non-UUID facility:', facility.id);
    return facility;
  }
  // ── Build the upsert payload — must include EVERY persisted field, otherwise
  // unspecified columns in Supabase get nulled on upsert. Bug history:
  // organization_id was missing here, which is why facility re-assignments
  // through the "Reassign" banner kept disappearing — the upsert wrote a row
  // without organization_id and Supabase nulled it. Same root cause for
  // cms_ccn, administrator, and contact fields all silently going blank
  // after edits. Add new persisted fields to this list as the schema grows.
  const payload = {
    id: facility.id,
    facility_name: facility.facility_name,
    facility_id: facility.facility_id || null,
    organization_id: facility.organization_id || null,
    cms_ccn: facility.cms_ccn || null,
    pcc_fac_id: facility.pcc_fac_id || null,
    address: facility.address || null,
    city: facility.city || null,
    state: facility.state || null,
    zip: facility.zip || null,
    phone: facility.phone || null,
    bed_count: facility.bed_count || null,
    facility_type: facility.facility_type || null,
    administrator: facility.administrator || null,
    contact_name: facility.contact_name || null,
    contact_title: facility.contact_title || null,
    contact_email: facility.contact_email || null,
    contact_phone: facility.contact_phone || null,
    require_poc_dates: typeof facility.require_poc_dates === 'boolean' ? facility.require_poc_dates : null,
  };
  let { data, error } = await supabase.from('facilities').upsert(payload, { onConflict: 'id' }).select().single();
  // Guard: if the pcc_fac_id column hasn't been added to the schema yet, don't
  // let it break every facility save — retry once without it. (Run the
  // facilities pcc_fac_id migration to actually persist the PCC mapping.)
  if (error && /pcc_fac_id/i.test(error.message || '')) {
    const { pcc_fac_id, ...rest } = payload;
    ({ data, error } = await supabase.from('facilities').upsert(rest, { onConflict: 'id' }).select().single());
  }
  if (error) { console.error('saveFacility:', error.message); return facility; }
  return data;
}

export async function deleteFacility(id) {
  if (!supabase) { ls.set('autopip_facilities', ls.get('autopip_facilities', []).filter(f => f.id !== id)); return { error: null }; }
  if (!isUUID(id)) return { error: { message: 'Invalid facility id' } };
  const { error } = await supabase.from('facilities').delete().eq('id', id);
  if (error) console.error('deleteFacility:', error.message);
  return { error };
}

// ─── ORGANIZATIONS ────────────────────────────────────────────────────────────
// Supabase's .delete() returns { data, error } rather than throwing on RLS /
// FK violations. Callers must check `error` — otherwise a "successful" UI
// delete silently fails server-side and the row reappears on next page load.
export async function deleteOrganization(id) {
  if (!supabase) return { error: null };
  const { error } = await supabase.from('organizations').delete().eq('id', id);
  if (error) console.error('deleteOrganization:', error.message);
  return { error };
}

export async function deleteFacilitiesByOrg(orgId) {
  if (!supabase) return { error: null };
  const { error } = await supabase.from('facilities').delete().eq('organization_id', orgId);
  if (error) console.error('deleteFacilitiesByOrg:', error.message);
  return { error };
}

// ─── POCS ─────────────────────────────────────────────────────────────────────
export async function getPOCs() {
  if (!supabase) return ls.get('autopip_pips', []);
  const { data, error } = await supabase.from('pocs').select('*').order('created_at', { ascending: false });
  if (error) { console.error('getPOCs:', error.message); return ls.get('autopip_pips', []); }
  return (data || []).map(dbToLocal);
}

export async function savePOC(poc) {
  if (!supabase) {
    const all = ls.get('autopip_pips', []);
    const idx = all.findIndex(p => p.id === poc.id);
    if (idx >= 0) all[idx] = poc; else all.push(poc);
    ls.set('autopip_pips', all);
    return poc;
  }
  if (!isUUID(poc.id)) {
    console.warn('Skipping non-UUID poc:', poc.id);
    return poc;
  }
  const row = localToDB(poc);
  const { data, error } = await supabase.from('pocs').upsert(row, { onConflict: 'id' }).select().single();
  if (error) { console.error('savePOC:', error.message); return poc; }
  return dbToLocal(data);
}

export async function deletePOC(id) {
  if (!supabase) { ls.set('autopip_pips', ls.get('autopip_pips', []).filter(p => p.id !== id)); return; }
  if (!isUUID(id)) return;
  await supabase.from('pocs').delete().eq('id', id);
}

// ─── TASKS ────────────────────────────────────────────────────────────────────
export async function getTasks() {
  if (!supabase) return ls.get('autopip_tasks', []);
  const { data, error } = await supabase.from('tasks').select('*').order('due_date');
  if (error) { console.error('getTasks:', error.message); return ls.get('autopip_tasks', []); }
  return (data || []).map(dbTaskToLocal);
}

export async function saveTask(task) {
  if (!supabase) {
    const all = ls.get('autopip_tasks', []);
    const idx = all.findIndex(t => t.id === task.id);
    if (idx >= 0) all[idx] = task; else all.push(task);
    ls.set('autopip_tasks', all);
    return task;
  }
  if (!isUUID(task.id)) { console.warn('Skipping non-UUID task:', task.id); return task; }
  const { data, error } = await supabase.from('tasks').upsert({
    id: task.id,
    poc_id: isUUID(task.pipId) ? task.pipId : null,
    facility_id: isUUID(task.facilityId) ? task.facilityId : null,
    label: task.label,
    section: task.section || null,
    owner: task.owner || null,
    due_date: task.dueDate || null,
    status: task.status || 'pending',
    completed_at: task.completedAt || null,
    auto_generated: task.autoGenerated || false,
    audit_task: task.auditTask || false,
    notes: task.notes || null,
  }, { onConflict: 'id' }).select().single();
  if (error) { console.error('saveTask:', error.message); return task; }
  return dbTaskToLocal(data);
}

export async function deleteTask(id) {
  if (!supabase) { ls.set('autopip_tasks', ls.get('autopip_tasks', []).filter(t => t.id !== id)); return; }
  if (!isUUID(id)) return;
  await supabase.from('tasks').delete().eq('id', id);
}

// ─── USERS ────────────────────────────────────────────────────────────────────
export async function getUsers() {
  if (!supabase) return ls.get('autopip_users', []);
  const { data, error } = await supabase.from('users').select('*').order('name');
  if (error) { console.error('getUsers:', error.message); return ls.get('autopip_users', []); }
  // Map snake_case back to camelCase
  return (data || []).map(u => ({
    ...u,
    jobRole: u.job_role,
    facilityIds: u.facility_ids || [],
    passwordHash: u.password_hash,
    lastLogin: u.last_login,
    createdAt: u.created_at,
    organizationId: u.organization_id,
    // Aliased so the flag has ONE name in the app. It was read in two places under two
    // spellings and written under neither, which is how "force a password change" came
    // to be a checkbox that did nothing.
    mustChangePassword: u.must_change_password === true,
  }));
}

export async function saveUser(user) {
  if (!supabase) {
    const all = ls.get('autopip_users', []);
    const idx = all.findIndex(u => u.id === user.id);
    if (idx >= 0) all[idx] = user; else all.push(user);
    ls.set('autopip_users', all);
    return user;
  }
  if (!isUUID(user.id)) { console.warn('Skipping non-UUID user:', user.id); return user; }
  const validFacilityIds = (user.facilityIds || []).filter(isUUID);
  const orgId = user.organizationId === undefined ? user.organization_id : user.organizationId;
  // THE FORCED-CHANGE FLAG, PRESERVED RATHER THAN DEFAULTED.
  //
  // Every saveUser caller passes a whole user record — deactivate, reactivate, a profile
  // edit — and most of them have no opinion about this flag. Writing `=== true` on a
  // record that simply does not carry it would send false, so deactivating somebody one
  // minute after an administrator unlocked them would silently cancel the forced
  // password change. So: the camelCase field wins when present, the snake_case column
  // that came back from the read is the fallback, and only an explicit value changes it.
  const mustChange = user.mustChangePassword === undefined
    ? user.must_change_password
    : user.mustChangePassword;
  const row = {
    id: user.id,
    email: user.email || null,
    name: user.name,
    title: user.title || null,
    job_role: user.jobRole || null,
    role: user.role,
    facility_ids: validFacilityIds,
    password_hash: user.passwordHash || null,
    active: user.active !== false,
    // organization_id round-trips so org membership survives saveUser writes.
    // Null is intentional — that's how we unassign a user from an org.
    //
    // NOT isUUID. THIS LINE WAS SILENTLY NULLING EVERY ORGANISATION.
    //
    // organizations.id is TEXT and holds slugs — org-ohio-mr0ps58s, org-pcc-sandbox,
    // org-test-facilit-mohpstza. The migration declares it uuid and the live column
    // drifted to text years ago; isUUID() returns false for every real value, so this
    // line discarded the organisation on EVERY save. The form collected it, handleAdd
    // passed it through correctly, and it was thrown away one line before the write.
    //
    // The consequence was not a missing field. A null organization_id MEANS
    // cross-organisation access in this application — App.js says so ("Cross-
    // organization access IS a null organization_id") and reads
    // `role === "admin" && !organization_id` as system admin. So every account saved
    // here quietly became cross-tenant, and the only thing standing between that and
    // one customer reading another's data was a policy called
    // "authenticated access (tenant scoping TODO)" that granted everything anyway.
    //
    // isUUID is still correct for facility.id and poc.id below — those columns really
    // are uuid. It was only ever wrong about organisations.
    organization_id: orgId ? String(orgId) : null,
    must_change_password: mustChange === true,
  };
  let { data, error } = await supabase.from('users').upsert(row, { onConflict: 'id' }).select().single();

  // ONE RETRY WITHOUT THE NEW COLUMN, and only for that one error.
  //
  // must_change_password is added by migration 035. Postgres rejects the WHOLE upsert
  // when a column does not exist, so a frontend deployed ahead of that migration would
  // fail every user write in this screen — creating, editing, deactivating, all of it —
  // reporting nothing but a console line. That is far worse than losing the flag.
  //
  // Narrow on purpose: it retries only when the message names this column, so a genuine
  // permission or constraint failure still surfaces as itself rather than being masked.
  if (error && /must_change_password/.test(error.message || '')) {
    console.warn(
      'saveUser: users.must_change_password is missing — apply migration 035. ' +
      'Saving without the forced-password-change flag.',
    );
    const { must_change_password: _omitted, ...withoutFlag } = row;
    ({ data, error } = await supabase.from('users').upsert(withoutFlag, { onConflict: 'id' }).select().single());
  }
  if (error) { console.error('saveUser:', error.message); return user; }
  return {
    ...data,
    jobRole: data.job_role,
    facilityIds: data.facility_ids || [],
    passwordHash: data.password_hash,
    organizationId: data.organization_id,
    // Same alias as the read, so the object handed back to a caller that keeps it in
    // state carries the flag under the name the rest of the app uses.
    mustChangePassword: data.must_change_password === true,
  };
}

export async function deleteUser(id) {
  if (!supabase) { ls.set('autopip_users', ls.get('autopip_users', []).filter(u => u.id !== id)); return; }
  if (!isUUID(id)) return;
  await supabase.from('users').delete().eq('id', id);
}

// ─── AUDIT LOG ────────────────────────────────────────────────────────────────
export async function getAuditLog(limit = 500) {
  if (!supabase) { try { return JSON.parse(localStorage.getItem('autopip_audit') || '[]'); } catch { return []; } }
  const { data, error } = await supabase.from('audit_log').select('*').order('created_at', { ascending: false }).limit(limit);
  if (error) { console.error('getAuditLog:', error.message); return []; }
  return (data || []).map(e => ({ ...e, timestamp: e.created_at, userEmail: e.user_email, userName: e.user_name, sectionsChanged: e.sections_changed, guidanceSource: e.guidance_source }));
}

// ─── WRITING THE TRAIL ────────────────────────────────────────────────────────
//
// THIS USED TO INSERT STRAIGHT INTO audit_log, WITH THE ACTOR AS A FIELD.
//
//     user_name: entry.user || null,
//     user_email: entry.userEmail || null,
//     browser: entry.browser || null,
//
// All three came out of this tab's own state, as did facility_name. Any account could
// record any action under anybody's name and the row would be indistinguishable from a
// real one, so the trail was evidence of what the app claimed rather than of what happened.
//
// And it could not fail usefully. The catch below used to be the whole error path:
//
//     } catch(e) { console.warn('audit log error:', e.message); }
//
// which is how SIX DAYS passed with no audit entries at all after the RDS cutover.
// `authenticated` held SELECT and nothing else on the table, every insert was refused, and
// the only trace was a console warning in whichever tab happened to be open.
//
// Now it posts to POST /api/audit, which takes the actor from the verified token and the
// browser from the User-Agent, and returns a status. See auditTrail.js in the API.
//
// THE LOCAL COPY IS STILL WRITTEN FIRST, and is now a retry queue rather than a consolation
// prize: an entry the server did not accept is kept and re-sent with the next one. An audit
// entry that exists only in one operator's localStorage is not an audit entry, but it is a
// great deal better than one that existed nowhere and said so to nobody.
const PENDING_AUDIT = 'survai_audit_pending';

async function postAuditEntry(entry, token) {
  const res = await fetch(`${API_BASE}/api/audit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    // Identity is deliberately NOT sent. The server would ignore it; not sending it means
    // nobody later reads this call and assumes it is honoured.
    body: JSON.stringify({
      id: isUUID(entry.id) ? entry.id : undefined,
      type: entry.type,
      category: entry.category || 'other',
      label: entry.label || entry.type,
      pocId: isUUID(entry.pipId) ? entry.pipId : null,
      facilityName: entry.facility || null,
      tags: entry.tags || [],
      sectionsChanged: entry.sectionsChanged || [],
      diffs: entry.diffs || null,
      guidanceSource: entry.guidanceSource || null,
      method: entry.method || null,
      outcome: entry.outcome || null,
      signerName: entry.signerName || null,
      extra: entry.extra || {},
    }),
  });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try { const b = await res.json(); detail = b.detail || b.error || detail; } catch {}
    throw new Error(detail);
  }
  return res;
}

export async function addAuditEntry(entry) {
  try { const ex = JSON.parse(localStorage.getItem('autopip_audit') || '[]'); localStorage.setItem('autopip_audit', JSON.stringify([entry, ...ex].slice(0, 1000))); } catch {}
  if (!supabase) return;

  let token = null;
  try {
    const { data } = await supabase.auth.getSession();
    token = data?.session?.access_token || null;
  } catch {}
  if (!token) {
    // Nobody is signed in, so there is no actor to record. Queueing would attribute the
    // action to whoever signs in next, which is worse than not recording it.
    console.warn('audit log: no session, entry kept locally only');
    return;
  }

  // Anything the server refused earlier goes first, oldest first, so the trail keeps its
  // order. Capped, because an operator offline for a week should not send a thousand-entry
  // burst on reconnect.
  let queue = ls.get(PENDING_AUDIT, []);
  const batch = [...(Array.isArray(queue) ? queue : []).slice(-50), entry];
  const stillPending = [];
  let firstError = null;

  for (const item of batch) {
    try {
      await postAuditEntry(item, token);
    } catch (e) {
      stillPending.push(item);
      if (!firstError) firstError = e;
    }
  }

  ls.set(PENDING_AUDIT, stillPending);
  if (firstError) {
    // Named, counted, and not swallowed. If this starts appearing, the trail is not
    // recording and somebody can see that it is not.
    console.error(
      `audit log: ${stillPending.length} entr${stillPending.length === 1 ? 'y' : 'ies'} not recorded ` +
      `(${firstError.message}); kept locally and retried with the next entry`,
    );
  }
}

// ─── POLICY LIBRARY (Supabase persistence — dual-write target) ───────────────
// These helpers write to the policies / policy_revisions / policy_audit
// tables defined in docs/policy-library-supabase-migration.sql. They are
// called fire-and-forget from the frontend localStorage helpers so the UI
// stays instant; Supabase is the source of truth, localStorage is the cache.
// If the migration hasn't been applied yet, all calls fall through
// gracefully (errors logged as warnings; the localStorage path is unaffected).

const _polLocalToDb = (p, facilityId) => ({
  id: p.id,
  facility_id: facilityId,
  code: p.code,
  name: p.name,
  category: p.category || null,
  ftags: p.ftags || [],
  owner: p.owner || null,
  last_reviewed: p.lastReviewed || null,
  last_modified: p.lastModified || null,
  notes: p.notes || null,
  body: p.body || null,
  version: p.version || '1.0',
  is_demo: !!p._demo,
});
const _polDbToLocal = (row) => ({
  id: row.id,
  code: row.code,
  name: row.name,
  category: row.category,
  ftags: row.ftags || [],
  owner: row.owner,
  lastReviewed: row.last_reviewed,
  lastModified: row.last_modified,
  notes: row.notes,
  body: row.body,
  version: row.version || '1.0',
  _demo: !!row.is_demo,
  updated: row.updated_at,
});
const _revLocalToDb = (r, facilityId) => ({
  id: r.id,
  facility_id: facilityId,
  policy_id: r.policyId || null,
  policy_code: r.policyCode || null,
  policy_name: r.policyName || null,
  citation_id: r.citationId || null,
  citation_tag: r.citationTag || null,
  citation_survey_date: r.citationSurveyDate || null,
  resident_ref: r.residentRef || null,
  procedure_ref: r.procedureRef || null,
  suggestion_before: r.suggestionBefore || null,
  suggestion_after: r.suggestionAfter || null,
  suggestion_text: r.suggestionText || null,
  rationale: r.rationale || null,
  status: r.status || 'pending',
  suggested_by: r.suggestedBy || 'ai',
  actioned_by: r.actionedBy || null,
  actioned_at: r.actionedAt || null,
  finalized_by: r.finalizedBy || null,
  finalized_at: r.finalizedAt || null,
  rejected_reason: r.rejectedReason || null,
  source_pip_id: r.sourcePipId || null,
  policy_snapshot_at: r.policySnapshotAt || null,
});

export async function savePoliciesDB(facilityId, policies) {
  if (!supabase || !facilityId) return { ok: false, reason: 'no-supabase-or-facility' };
  try {
    const rows = (policies || []).map(p => _polLocalToDb(p, facilityId));
    if (rows.length === 0) return { ok: true, count: 0 };
    const { error } = await supabase.from('policies').upsert(rows, { onConflict: 'id' });
    if (error) { console.warn('savePoliciesDB:', error.message); return { ok: false, reason: error.message }; }
    return { ok: true, count: rows.length };
  } catch (e) { console.warn('savePoliciesDB threw:', e?.message); return { ok: false, reason: e?.message }; }
}

export async function getPoliciesDB(facilityId) {
  if (!supabase || !facilityId) return null;
  try {
    const { data, error } = await supabase.from('policies').select('*').eq('facility_id', facilityId).order('code');
    if (error) { console.warn('getPoliciesDB:', error.message); return null; }
    return (data || []).map(_polDbToLocal);
  } catch (e) { console.warn('getPoliciesDB threw:', e?.message); return null; }
}

export async function deletePoliciesDB(facilityId, ids) {
  if (!supabase || !facilityId || !ids || ids.length === 0) return { ok: true };
  try {
    const { error } = await supabase.from('policies').delete().eq('facility_id', facilityId).in('id', ids);
    if (error) { console.warn('deletePoliciesDB:', error.message); return { ok: false }; }
    return { ok: true };
  } catch (e) { console.warn('deletePoliciesDB threw:', e?.message); return { ok: false }; }
}

export async function saveRevisionsDB(facilityId, revisions) {
  if (!supabase || !facilityId) return { ok: false };
  try {
    const rows = (revisions || []).map(r => _revLocalToDb(r, facilityId));
    if (rows.length === 0) return { ok: true, count: 0 };
    const { error } = await supabase.from('policy_revisions').upsert(rows, { onConflict: 'id' });
    if (error) { console.warn('saveRevisionsDB:', error.message); return { ok: false }; }
    return { ok: true, count: rows.length };
  } catch (e) { console.warn('saveRevisionsDB threw:', e?.message); return { ok: false }; }
}

export async function getRevisionsDB(facilityId) {
  if (!supabase || !facilityId) return null;
  try {
    const { data, error } = await supabase.from('policy_revisions').select('*').eq('facility_id', facilityId).order('created_at', { ascending: false });
    if (error) { console.warn('getRevisionsDB:', error.message); return null; }
    return (data || []).map(r => ({
      id: r.id, policyId: r.policy_id, policyCode: r.policy_code, policyName: r.policy_name,
      citationId: r.citation_id, citationTag: r.citation_tag, citationSurveyDate: r.citation_survey_date,
      residentRef: r.resident_ref, procedureRef: r.procedure_ref,
      suggestionBefore: r.suggestion_before, suggestionAfter: r.suggestion_after,
      suggestionText: r.suggestion_text, rationale: r.rationale,
      status: r.status, suggestedBy: r.suggested_by,
      actionedBy: r.actioned_by, actionedAt: r.actioned_at,
      finalizedBy: r.finalized_by, finalizedAt: r.finalized_at,
      rejectedReason: r.rejected_reason,
      sourcePipId: r.source_pip_id, policySnapshotAt: r.policy_snapshot_at,
      created: r.created_at,
    }));
  } catch (e) { console.warn('getRevisionsDB threw:', e?.message); return null; }
}

export async function appendPolicyAuditDB(entry) {
  if (!supabase || !entry || !entry.facilityId) return { ok: false };
  try {
    const { error } = await supabase.from('policy_audit').insert({
      id: entry.id,
      facility_id: entry.facilityId,
      policy_id: entry.policyId || null,
      revision_id: entry.revisionId || null,
      action: entry.type || entry.action || 'POLICY_EVENT',
      user_name: entry.user || null,
      user_email: entry.userEmail || null,
      details: entry.extra || entry.details || {},
      created_at: entry.timestamp || new Date().toISOString(),
    });
    if (error) { console.warn('appendPolicyAuditDB:', error.message); return { ok: false }; }
    return { ok: true };
  } catch (e) { console.warn('appendPolicyAuditDB threw:', e?.message); return { ok: false }; }
}

// ─── SUCCESS LIBRARY ──────────────────────────────────────────────────────────
export async function getSuccessLibraryDB() {
  if (!supabase) { try { return JSON.parse(localStorage.getItem('survai_success_library') || '[]'); } catch { return []; } }
  const { data, error } = await supabase.from('success_library').select('*').order('added_at', { ascending: false });
  if (error) { return []; }
  return data || [];
}

export async function addToSuccessLibraryDB(entry) {
  if (!supabase) return;
  try {
    if (!isUUID(entry.id)) { console.warn('Skipping non-UUID success library entry:', entry.id); return; }
    await supabase.from('success_library').upsert({
      id: entry.id,
      poc_id: isUUID(entry.id) ? entry.id : null,
      facility_name: entry.facility || null,
      tags: entry.tags || [],
      survey_date: entry.surveyDate || null,
      survey_type: entry.surveyType || null,
      scope_severity: entry.scopeSeverity || null,
      archive_outcome: entry.archiveOutcome || null,
      archive_date: entry.archiveDate || null,
      archive_notes: entry.archiveNotes || null,
      sections: entry.sections || {},
      citation_data: entry.citationData || {},
      task_summary: entry.taskSummary || [],
    }, { onConflict: 'id' });
  } catch(e) { console.warn('success library error:', e.message); }
}

// ─── ARCHIVE BUNDLE ───────────────────────────────────────────────────────────
export async function addToArchiveBundleDB(bundle) {
  if (!supabase) return;
  try {
    const archiveId = bundle.archiveId || bundle.id || null;
    if (!isUUID(archiveId)) { console.warn('Skipping non-UUID archive bundle:', archiveId); return; }
    await supabase.from('archive_bundles').upsert({
      id: archiveId,
      poc_id: isUUID(bundle.id) ? bundle.id : null,
      facility_name: bundle.facility || null,
      tags: bundle.tags || [],
      survey_date: bundle.surveyDate || null,
      survey_type: bundle.surveyType || null,
      scope_severity: bundle.scopeSeverity || null,
      outcome: bundle.archiveOutcome || null,
      archived_at: bundle.archivedAt || new Date().toISOString(),
      bundle: bundle,
    }, { onConflict: 'id' });
  } catch(e) { console.warn('archive bundle error:', e.message); }
}

export async function getArchiveBundlesDB() {
  if (!supabase) { try { return JSON.parse(localStorage.getItem('survai_archive_bundle') || '[]'); } catch { return []; } }
  const { data, error } = await supabase.from('archive_bundles').select('*').order('archived_at', { ascending: false });
  if (error) { return []; }
  return (data || []).map(r => ({ ...r.bundle, id: r.poc_id }));
}

// ─── DENIAL SIGNALS ───────────────────────────────────────────────────────────
// Structured rejection feedback captured when a POC is marked denied or
// revision-needed. Feeds the avoid-patterns block in the POC generator.
// Mirrors the success_library pattern; falls back to localStorage when
// Supabase is unavailable or the `denial_signals` table doesn't exist yet.
export async function getDenialSignalsDB() {
  if (!supabase) { try { return JSON.parse(localStorage.getItem('survai_denial_signals') || '[]'); } catch { return []; } }
  const { data, error } = await supabase.from('denial_signals').select('*').order('captured_at', { ascending: false });
  if (error) { return []; }
  return data || [];
}

export async function addDenialSignalDB(signal) {
  if (!supabase) return;
  try {
    if (!isUUID(signal.id)) { console.warn('Skipping non-UUID denial signal:', signal.id); return; }
    await supabase.from('denial_signals').upsert({
      id: signal.id,
      poc_id: isUUID(signal.pipId) ? signal.pipId : null,
      facility_name: signal.facility || null,
      facility_state: signal.facility_state || null,
      tags: signal.tags || [],
      outcome: signal.outcome || null,
      rejected_sections: signal.rejectedSections || [],
      categories: signal.categories || [],
      surveyor_quote: signal.surveyorQuote || null,
      captured_at: signal.capturedAt || new Date().toISOString(),
      captured_by: signal.capturedBy || null,
    }, { onConflict: 'id' });
  } catch(e) { console.warn('denial signal error:', e.message); }
}

// ─── HELPERS ─────────────────────────────────────────────────────────────────
function localToDB(poc) {
  return {
    id: poc.id,
    facility_id: isUUID(poc.facility_id) ? poc.facility_id : null,
    facility_name: poc.facility || null,
    tags: poc.tags || [],
    survey_date: poc.surveyDate || null,
    survey_type: poc.surveyType || null,
    scope_severity: poc.scopeSeverity || null,
    status: poc.status || 'Draft',
    mode: poc.mode || 'AI',
    mode_reason: poc.modeReason || null,
    batch_id: poc.batchId || null,
    batch_label: poc.batchLabel || null,
    sections: poc.sections || {},
    citation_data: poc.citationData || {},
    guidance_used: poc.guidanceUsed || [],
    signers: poc.signers || [],
    cover_letter: poc.coverLetter || null,
    archive_outcome: poc.archiveOutcome || 'pending',
    archive_date: poc.archiveDate || null,
    archive_notes: poc.archiveNotes || null,
    archive_ref: poc.archiveRef || null,
    archived_off: poc.archivedOff || false,
    export_history: poc.exportHistory || [],
    submission_log: poc.submissionLog || [],
    version_history: poc.versionHistory || [],
    source_document: poc.sourceDocument || null,
  };
}

function dbToLocal(row) {
  return {
    id: row.id,
    facility_id: row.facility_id,
    facility: row.facility_name,
    tags: row.tags || [],
    surveyDate: row.survey_date,
    surveyType: row.survey_type,
    scopeSeverity: row.scope_severity,
    status: row.status,
    mode: row.mode,
    modeReason: row.mode_reason,
    batchId: row.batch_id,
    batchLabel: row.batch_label,
    sections: row.sections || {},
    citationData: row.citation_data || {},
    guidanceUsed: row.guidance_used || [],
    signers: row.signers || [],
    coverLetter: row.cover_letter,
    archiveOutcome: row.archive_outcome || 'pending',
    archiveDate: row.archive_date,
    archiveNotes: row.archive_notes,
    archiveRef: row.archive_ref,
    archivedOff: row.archived_off || false,
    exportHistory: row.export_history || [],
    submissionLog: row.submission_log || [],
    versionHistory: row.version_history || [],
    sourceDocument: row.source_document,
    createdDate: row.created_at?.split('T')[0],
  };
}

function dbTaskToLocal(row) {
  return {
    id: row.id,
    pipId: row.poc_id,
    facilityId: row.facility_id,
    label: row.label,
    section: row.section,
    owner: row.owner,
    dueDate: row.due_date,
    status: row.status,
    completedAt: row.completed_at,
    autoGenerated: row.auto_generated,
    auditTask: row.audit_task,
    notes: row.notes,
  };
}
